import type {
  DocumentSchema,
  SearchResponse,
} from 'typesense/lib/Typesense/Documents';
import type { SetlistContext } from './client';

import { STOCK_VIDEO_COLLECTION } from './collections';
import { buildCategoryKeywordFilter } from './get-category-query-defaults';
import {
  FREE_ASSET_RATIO,
  STOCK_FOOTAGE_SEARCH_WEIGHTS,
} from './stock-video-constants';
import {
  composeStockVideoSort,
  DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  STOCK_VIDEO_INCLUDE_FIELDS,
  VIDEO_RECENCY_TIE_BREAKER,
  withEngagementFilter,
} from './stock-video-engagement-join';
import { VECTOR_SEARCH_DEFAULTS } from './vector-search';

export interface AssetCounts {
  /** True when premium/free were estimated from page hits (no facet data). */
  estimated?: boolean;
  free: number;
  premium: number;
  total: number;
}

export interface OrientationFacetCount {
  count: number;
  value: string;
}

export type SearchResponseWithCounts = SearchResponse<DocumentSchema> & {
  assetCounts?: AssetCounts;
  orientationFacets?: OrientationFacetCount[];
};

/**
 * Fetches stock footage videos similar to a given category using vector search.
 * Uses multi-search endpoint (POST) to avoid URL length limits with large embeddings.
 *
 * Inline search terms are applied via filter_by (see buildSearchFilter in the
 * infinite query hook) so results always stay within the category embedding space.
 *
 * When `unionKeywords` is provided, performs a Typesense union search that combines
 * vector results with an exact keyword match search, ensuring videos tagged with
 * matching keywords always appear regardless of vector distance.
 *
 * Engagement data from videoEngagement.v1 is left-joined to sort results by relevance.
 * For subscribed users, paid engagement is used for all results.
 * For free / logged-out users, separate queries fetch premium assets (with paid
 * engagement) and free assets (with free engagement), then merge the results.
 */
export const getStockFootageByCategoryEmbedding = async (
  context: SetlistContext,
  {
    categoryEmbedding,
    page = 1,
    perPage = 16,
    filterBy,
    distanceThreshold = VECTOR_SEARCH_DEFAULTS.VIDEO.DISTANCE_THRESHOLD_DEFAULT,
    unionKeywords,
    isSubscribedUser = false,
    sortBy,
    slug = DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  }: {
    categoryEmbedding?: number[];
    page?: number;
    perPage?: number;
    filterBy?: string;
    distanceThreshold?: number;
    unionKeywords?: string[];
    isSubscribedUser?: boolean;
    sortBy?: string;
    slug?: string;
  }
) => {
  if (!categoryEmbedding || categoryEmbedding.length === 0) {
    return getTextFallbackSearch(context, {
      page,
      perPage,
      filterBy,
      isSubscribedUser,
      sortBy,
      slug,
    });
  }

  if (isSubscribedUser) {
    // Subscribed users: single search with paid engagement + facet for counts
    const result = await getEmbeddingSearch(context, {
      categoryEmbedding,
      page,
      perPage,
      filterBy,
      distanceThreshold,
      unionKeywords,
      accountTypeGroup: 'paid',
      facetBy: 'is_premium,orientation',
      sortBy,
      slug,
    });
    return attachFacetCounts(result);
  }

  // Free / logged-out users: split into premium + free legs
  // Premium assets use paid engagement, free assets use free engagement
  const premiumFilterBy = filterBy
    ? `${filterBy} && is_premium:=true`
    : 'is_premium:=true';
  const freeFilterBy = filterBy
    ? `${filterBy} && is_premium:=false`
    : 'is_premium:=false';

  const [premiumResults, freeResults] = await Promise.all([
    getEmbeddingSearch(context, {
      categoryEmbedding,
      page,
      perPage,
      filterBy: premiumFilterBy,
      distanceThreshold,
      unionKeywords,
      accountTypeGroup: 'paid',
      facetBy: 'orientation',
      sortBy,
      slug,
    }),
    getEmbeddingSearch(context, {
      categoryEmbedding,
      page,
      perPage,
      filterBy: freeFilterBy,
      distanceThreshold,
      unionKeywords,
      accountTypeGroup: 'free',
      facetBy: 'orientation',
      sortBy,
      slug,
    }),
  ]);

  return mergeSearchResponses(premiumResults, freeResults, perPage);
};

/**
 * Fallback text search when no category embedding is available.
 */
const getTextFallbackSearch = async (
  context: SetlistContext,
  {
    page,
    perPage,
    filterBy,
    isSubscribedUser,
    sortBy,
    slug = DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  }: {
    page: number;
    perPage: number;
    filterBy?: string;
    isSubscribedUser: boolean;
    sortBy?: string;
    slug?: string;
  }
) => {
  const textFallbackSort = composeStockVideoSort([
    sortBy,
    VIDEO_RECENCY_TIE_BREAKER,
  ]);

  if (isSubscribedUser) {
    const result = await context.client
      .collections(STOCK_VIDEO_COLLECTION)
      .documents()
      .search(
        {
          q: '*',
          query_by: 'keywords,title,description',
          query_by_weights: `keywords:${STOCK_FOOTAGE_SEARCH_WEIGHTS.KEYWORDS},title:${STOCK_FOOTAGE_SEARCH_WEIGHTS.TITLE},description:${STOCK_FOOTAGE_SEARCH_WEIGHTS.DESCRIPTION}`,
          page,
          per_page: perPage,
          prefix: false,
          include_fields: STOCK_VIDEO_INCLUDE_FIELDS,
          filter_by: withEngagementFilter('paid', filterBy, slug),
          sort_by: textFallbackSort,
          facet_by: 'is_premium,orientation',
        },
        { cacheSearchResultsForSeconds: 0 }
      );
    return attachFacetCounts(result);
  }

  // Free / logged-out: split into premium + free
  const premiumFilter = filterBy
    ? `${filterBy} && is_premium:=true`
    : 'is_premium:=true';
  const freeFilter = filterBy
    ? `${filterBy} && is_premium:=false`
    : 'is_premium:=false';

  const [premiumResults, freeResults] = await Promise.all([
    context.client
      .collections(STOCK_VIDEO_COLLECTION)
      .documents()
      .search(
        {
          q: '*',
          query_by: 'keywords,title,description',
          query_by_weights: `keywords:${STOCK_FOOTAGE_SEARCH_WEIGHTS.KEYWORDS},title:${STOCK_FOOTAGE_SEARCH_WEIGHTS.TITLE},description:${STOCK_FOOTAGE_SEARCH_WEIGHTS.DESCRIPTION}`,
          page,
          per_page: perPage,
          prefix: false,
          include_fields: STOCK_VIDEO_INCLUDE_FIELDS,
          filter_by: withEngagementFilter('paid', premiumFilter, slug),
          sort_by: textFallbackSort,
          facet_by: 'orientation',
        },
        { cacheSearchResultsForSeconds: 0 }
      ),
    context.client
      .collections(STOCK_VIDEO_COLLECTION)
      .documents()
      .search(
        {
          q: '*',
          query_by: 'keywords,title,description',
          query_by_weights: `keywords:${STOCK_FOOTAGE_SEARCH_WEIGHTS.KEYWORDS},title:${STOCK_FOOTAGE_SEARCH_WEIGHTS.TITLE},description:${STOCK_FOOTAGE_SEARCH_WEIGHTS.DESCRIPTION}`,
          page,
          per_page: perPage,
          prefix: false,
          include_fields: STOCK_VIDEO_INCLUDE_FIELDS,
          filter_by: withEngagementFilter('free', freeFilter, slug),
          sort_by: textFallbackSort,
          facet_by: 'orientation',
        },
        { cacheSearchResultsForSeconds: 0 }
      ),
  ]);

  return mergeSearchResponses(premiumResults, freeResults, perPage);
};

/**
 * Embedding-based search (vector or union) with engagement left join.
 */
const getEmbeddingSearch = async (
  context: SetlistContext,
  {
    categoryEmbedding,
    page,
    perPage,
    filterBy,
    distanceThreshold,
    unionKeywords,
    accountTypeGroup,
    facetBy,
    sortBy,
    slug = DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  }: {
    categoryEmbedding: number[];
    page: number;
    perPage: number;
    filterBy?: string;
    distanceThreshold: number;
    unionKeywords?: string[];
    accountTypeGroup: 'paid' | 'free';
    facetBy?: string;
    sortBy?: string;
    slug?: string;
  }
) => {
  const vectorParams = `distance_threshold:${distanceThreshold}`;
  const engagementFilterBy = withEngagementFilter(
    accountTypeGroup,
    filterBy,
    slug
  );

  const vectorSearch = {
    collection: STOCK_VIDEO_COLLECTION,
    q: '*',
    vector_query: `embedding:([${categoryEmbedding.join(',')}], ${vectorParams})`,
    prefix: false,
    include_fields: STOCK_VIDEO_INCLUDE_FIELDS,
    filter_by: engagementFilterBy,
    sort_by: sortBy,
    ...(facetBy ? { facet_by: facetBy } : {}),
  };

  // Union mode: combine vector + keyword search
  if (unionKeywords && unionKeywords.length > 0) {
    const keywordFilter = buildCategoryKeywordFilter(unionKeywords);
    const combinedKeywordFilter = filterBy
      ? `${filterBy} && (${keywordFilter})`
      : keywordFilter;

    // The keyword search must include a vector_query so Typesense adds
    // _vector_distance to both legs' sort fields (union requires matching
    // sort_by sets). Use distance_threshold:1 so no keyword-matched results
    // are excluded by vector distance — the whole point is to surface these
    // regardless of how far they are from the category embedding.
    const keywordSearch = {
      collection: STOCK_VIDEO_COLLECTION,
      q: '*',
      vector_query: `embedding:([${categoryEmbedding.join(',')}], distance_threshold:1)`,
      prefix: false,
      include_fields: STOCK_VIDEO_INCLUDE_FIELDS,
      filter_by: withEngagementFilter(
        accountTypeGroup,
        combinedKeywordFilter,
        slug
      ),
    };

    // engagement sort → interleave union legs → recency tie-break for any
    // remaining ties (e.g. unranked videos with identical set_live values).
    // composeStockVideoSort caps at Typesense's 3-field sort limit.
    const unionSort = composeStockVideoSort([
      sortBy,
      '_union_search_index:asc',
      VIDEO_RECENCY_TIE_BREAKER,
    ]);

    const unionResponse = await context.client.multiSearch.perform(
      {
        union: true,
        searches: [
          { ...vectorSearch, sort_by: unionSort },
          { ...keywordSearch, sort_by: unionSort },
        ],
      },
      { page, per_page: perPage },
      { cacheSearchResultsForSeconds: 60 }
    );

    // UnionSearchResponse has the same hits/grouped_hits shape as SearchResponse
    // but replaces request_params with union_request_params. Cast for compatibility
    // with downstream utilities (extractHits, getSearchResultCount).
    return unionResponse as unknown as SearchResponse<DocumentSchema>;
  }

  // Non-union vector search: engagement sort_score → embedding proximity → recency.
  // _vector_distance must sit between sort_score and set_live so embedding
  // distance gets the next say after engagement; otherwise set_live's variance
  // would consume every tie before _vector_distance was consulted.
  // composeStockVideoSort caps at Typesense's 3-field sort limit.
  const multiSearchResponse = await context.client.multiSearch.perform(
    {
      searches: [
        {
          ...vectorSearch,
          sort_by: composeStockVideoSort([
            sortBy,
            '_vector_distance:asc',
            VIDEO_RECENCY_TIE_BREAKER,
          ]),
          page,
          per_page: perPage,
        },
      ],
    },
    {},
    { cacheSearchResultsForSeconds: 60 }
  );

  return multiSearchResponse.results[0];
};

/**
 * Extract is_premium facet counts from a Typesense response and attach as assetCounts.
 */
const extractOrientationFacets = (
  response: SearchResponse<DocumentSchema>
): OrientationFacetCount[] => {
  const facet = response.facet_counts?.find(
    (f) => f.field_name === 'orientation'
  );
  if (facet?.counts) {
    return facet.counts.map((c) => ({ value: c.value, count: c.count }));
  }

  // Union responses don't include facet_counts — estimate from hits.
  // We only need to know which orientations exist (count > 0) for disabling toggles.
  const orientationMap = new Map<string, number>();
  for (const hit of response.hits ?? []) {
    const orientation = (hit.document as Record<string, unknown>)[
      'orientation'
    ] as string | undefined;
    if (orientation) {
      orientationMap.set(
        orientation,
        (orientationMap.get(orientation) ?? 0) + 1
      );
    }
  }
  return Array.from(orientationMap, ([value, count]) => ({ value, count }));
};

const attachFacetCounts = (
  response: SearchResponse<DocumentSchema>
): SearchResponseWithCounts => {
  const total = response.found ?? 0;
  const orientationFacets = extractOrientationFacets(response);
  const facet = response.facet_counts?.find(
    (f) => f.field_name === 'is_premium'
  );

  if (facet) {
    let premium = 0;
    let free = 0;
    for (const val of facet.counts) {
      if (val.value === 'true') {
        premium = val.count;
      } else if (val.value === 'false') {
        free = val.count;
      }
    }
    return {
      ...response,
      assetCounts: { total, premium, free },
      orientationFacets,
    };
  }

  // Union responses don't include facet_counts — estimate from current page hits.
  // Raw per-page counts are stored here; the client-side select aggregates across
  // all loaded pages for a larger sample before extrapolating to the total.
  let premium = 0;
  let free = 0;
  for (const hit of response.hits ?? []) {
    if ((hit.document as Record<string, unknown>)['is_premium'] === true) {
      premium++;
    } else {
      free++;
    }
  }
  return {
    ...response,
    assetCounts: { total, premium, free, estimated: true },
    orientationFacets,
  };
};

const mergeOrientationFacets = (
  a: SearchResponse<DocumentSchema>,
  b: SearchResponse<DocumentSchema>
): OrientationFacetCount[] => {
  const map = new Map<string, number>();
  for (const resp of [a, b]) {
    for (const fc of extractOrientationFacets(resp)) {
      map.set(fc.value, (map.get(fc.value) ?? 0) + fc.count);
    }
  }
  return Array.from(map, ([value, count]) => ({ value, count }));
};

/**
 * Merge two SearchResponse objects by interleaving their hits using a fixed
 * free/premium ratio ({@link FREE_ASSET_RATIO}).
 *
 * Each leg's hits are already sorted by their respective engagement scores,
 * but premium (paid engagement) and free (free engagement) scores are not
 * directly comparable — sorting them together would let one type dominate.
 *
 * Instead, page slots are allocated using a fixed ratio (currently 60% free,
 * 40% premium), rounded to the nearest integer. Each side is guaranteed at
 * least 1 slot when both have results. If one side has fewer hits than its
 * allocation, the other side fills the remaining slots. Within each type,
 * the engagement-based ordering is preserved.
 */
export const mergeSearchResponses = (
  premiumResponse: SearchResponse<DocumentSchema>,
  freeResponse: SearchResponse<DocumentSchema>,
  perPage: number
): SearchResponseWithCounts => {
  const premiumHits = premiumResponse.hits ?? [];
  const freeHits = freeResponse.hits ?? [];
  const orientationFacets = mergeOrientationFacets(
    premiumResponse,
    freeResponse
  );

  const premiumTotal = premiumResponse.found ?? 0;
  const freeTotal = freeResponse.found ?? 0;
  const totalFound = premiumTotal + freeTotal;

  // If one side is empty, just return the other (sliced to perPage)
  if (premiumHits.length === 0) {
    return {
      ...freeResponse,
      found: totalFound,
      hits: freeHits.slice(0, perPage),
      out_of: (premiumResponse.out_of ?? 0) + (freeResponse.out_of ?? 0),
      assetCounts: {
        total: totalFound,
        premium: premiumTotal,
        free: freeTotal,
      },
      orientationFacets,
    };
  }
  if (freeHits.length === 0) {
    return {
      ...premiumResponse,
      found: totalFound,
      hits: premiumHits.slice(0, perPage),
      out_of: (premiumResponse.out_of ?? 0) + (freeResponse.out_of ?? 0),
      assetCounts: {
        total: totalFound,
        premium: premiumTotal,
        free: freeTotal,
      },
      orientationFacets,
    };
  }

  // Allocate slots using a fixed free/premium ratio, guaranteeing at least 1 per side
  const initialFreeSlots = Math.max(
    1,
    Math.min(freeHits.length, Math.round(FREE_ASSET_RATIO * perPage))
  );
  const premiumSlots = Math.max(
    1,
    Math.min(premiumHits.length, perPage - initialFreeSlots)
  );
  // Re-adjust free after premiumSlots is clamped to its final value
  const freeSlots = Math.min(freeHits.length, perPage - premiumSlots);

  // Take the top N from each leg (already sorted by their own engagement scores)
  const selectedPremium = premiumHits.slice(0, premiumSlots);
  const selectedFree = freeHits.slice(0, freeSlots);

  // Interleave: distribute the minority evenly through the majority
  const merged: typeof premiumHits = [];
  const [major, minor] =
    selectedPremium.length >= selectedFree.length
      ? [selectedPremium, selectedFree]
      : [selectedFree, selectedPremium];

  // step = major.length / minor.length gives the ideal spacing between minor
  // items. We insert a minor item once the loop index reaches the rounded
  // position for the next minor slot, spreading them evenly through major.
  const step =
    minor.length > 0 ? major.length / minor.length : Number.POSITIVE_INFINITY;
  let minorIdx = 0;

  for (let i = 0; i < major.length; i++) {
    merged.push(major[i]);
    if (minorIdx < minor.length && i >= Math.round(step * (minorIdx + 1)) - 1) {
      merged.push(minor[minorIdx]);
      minorIdx++;
    }
  }
  // Append any remaining minor hits
  while (minorIdx < minor.length) {
    merged.push(minor[minorIdx]);
    minorIdx++;
  }

  return {
    ...premiumResponse,
    found: totalFound,
    hits: merged.slice(0, perPage),
    out_of: (premiumResponse.out_of ?? 0) + (freeResponse.out_of ?? 0),
    assetCounts: {
      total: totalFound,
      premium: premiumTotal,
      free: freeTotal,
    },
    orientationFacets,
  };
};
